import { Component, OnInit, OnDestroy, Input } from '@angular/core';

import {
  of,
  tap,
  filter,
  switchMap,
  catchError,
  Subscription,
  BehaviorSubject,
  Observable,
  combineLatestWith,
  map,
  take,
} from 'rxjs';

import { Location } from '@shared/location/models';
import { Invite } from '@shared/invite/models';
import { InviteStatus, InviteRole } from '@shared/invite/enums';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  DropdownFilterItem,
} from '@tablesready/common-ui';
import { InviteApiService } from '../../../../../services';
import { InviteStatuses, InviteStatusesMap } from '../../../../constants';
import { InviteUserComponent } from '../../modals';
import { InvitesFilters } from '../../models';
import { InviteRoles } from './constants';
import { InvitesService, LocationUserListService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { InviteRolesMap } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-location-invites-list',
  templateUrl: './location-invites-list.component.html',
  styleUrls: ['./location-invites-list.component.scss'],
})
export class LocationInvitesListComponent implements OnInit, OnDestroy {
  @Input() timezone = '';
  page = 1;
  pageSize = 10;
  maxSize = 10;
  invites$: Observable<Invite[]>;
  inviteStatus = InviteStatus;
  locations: Location[];
  inviteStatusesMap = InviteStatusesMap;
  inviteRolesMap = InviteRolesMap;
  inviteRoles = InviteRoles;
  inviteStatuses = InviteStatuses;
  inviteUserFilter?: string;
  statusFilter?: string;

  filters$ = new BehaviorSubject<InvitesFilters>({
    email: '',
    role: null,
    status: null,
  });

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly modalController: FrontendModalService,
    private readonly invitesService: InvitesService,
    private readonly inviteApiService: InviteApiService,
    private readonly locationUserListService: LocationUserListService,
  ) {
  }

  ngOnInit(): void {
    this.invites$ = this.invitesService.filter(this.filters$);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  inviteUser(): void {
    this.modalController.create<InviteUserComponent, unknown>(InviteUserComponent, {
      data: this.locationUserListService.getDataForInvite()
        .pipe(
          combineLatestWith(this.invitesService.selectAll()),
          map(([[locationId, users, accountUsers, admins, locationUsers], invites]) => [locationId, users, accountUsers, admins, locationUsers, invites]),
        ),
    })
      .pipe(take(1))
      .subscribe();
  }

  resend({id, email}: Invite): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: `Resend invite (${email})`,
        content: 'Are you sure to resend this invite?',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.inviteApiService.resend(id)),
        tap(() => this.alertsService.addSuccess('resent invite', 'Success: ')),
        catchError(() => {
          this.alertsService.addError('failed resend invite');

          return of(null);
        }),
      )
      .subscribe();
  }

  cancel({id, email}: Invite): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: `Cancel invite (${email})`,
        content: 'Are you sure to cancel this invite?',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.inviteApiService.remove(id)),
        tap(() => this.alertsService.addSuccess('invite canceled', 'Success: ')),
        catchError(() => {
          this.alertsService.addError('failed cancel invite');

          return of(null);
        }),
      )
      .subscribe();
  }

  searchInvite(email: string): void {
    let filters = this.filters;
    filters = { ...filters, email };

    this.filters$.next(filters);
  }

  setFilters(item: Partial<InvitesFilters>): void {
    this.filters$.next({...this.filters, ...item});
  }

  get filters(): InvitesFilters {
    return this.filters$.getValue();
  }

  get start(): number {
    return (this.page - 1) * this.pageSize;
  }

  get end(): number {
    return (this.page - 1) * this.pageSize + this.pageSize;
  }
}
